"use client";

import type { JsonInputType } from "@tosize/shapes/src/lib/models/Input/JsonInputType";
import { getErrorMap } from "@tosize/ui";
import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { SHAPE_DIMENSIONS_STEP_IDENTIFIER } from "../components/Form/Steps/ShapeDimensions";
import { THICKNESS_STEP_IDENTIFIER } from "../components/Form/Steps/Thickness";
import { useCardContext } from "./CardContext";
import {
	ErrorContext,
	type ErrorsType,
	type FieldErrorType,
	type KeyOfErrorsType,
	type TouchedFieldsType,
} from "./ErrorContext";

type Props = PropsWithChildren;

export const ErrorContextProvider: FC<Props> = ({ children }) => {
	const intl = useIntl();
	const { setInvalidCards } = useCardContext();

	const [errors, setErrors] = useState<ErrorsType>({
		product: [],
		shape: [],
		dimensions: {},
		accepted_condition: [],
	});

	const [touchedFields, setTouchedFields] = useState<TouchedFieldsType>({
		product: [],
		shape: [],
		dimensions: [],
		accepted_condition: [],
	});

	const resetErrors = useCallback((field: KeyOfErrorsType) => {
		setErrors((prev) => ({ ...prev, [field]: {} }));
	}, []);

	const addErrors = useCallback((field: KeyOfErrorsType, errors: FieldErrorType) => {
		setErrors((prev) => ({ ...prev, [field]: errors }));
	}, []);

	const errorMap = useMemo(() => getErrorMap(intl, "mmToCm"), [intl]);

	const hasErrors = useMemo(() => {
		return (
			errors.product.length > 0 ||
			errors.shape.length > 0 ||
			Object.values(errors.dimensions).some((error) => error.length > 0)
		);
	}, [errors]);

	useEffect(() => {
		const cards: string[] = [];
		const Types: Record<KeyOfErrorsType, string> = {
			product: THICKNESS_STEP_IDENTIFIER,
			shape: SHAPE_DIMENSIONS_STEP_IDENTIFIER,
			dimensions: SHAPE_DIMENSIONS_STEP_IDENTIFIER,
			accepted_condition: SHAPE_DIMENSIONS_STEP_IDENTIFIER,
		};

		// check for each type if it has errors and if that field is also touched
		for (const [errorType, identifier] of Object.entries(Types)) {
			const type = errorType as KeyOfErrorsType;
			const errorFields = Array.isArray(errors[type])
				? errors[type].length
					? [type]
					: []
				: Object.keys(errors[type]);
			const touched = touchedFields[type];

			if (!!errorFields.length && !!touched.length) {
				if (errorFields.filter((field) => touched.includes(field as JsonInputType)).length) {
					cards.push(identifier);
				}
			}
		}

		setInvalidCards(cards);
	}, [errors, setInvalidCards, touchedFields]);

	return (
		<ErrorContext.Provider
			value={{
				hasErrors,
				errorMap,
				errors,
				setErrors,
				resetErrors,
				addErrors,

				touchedFields,
				setTouchedFields,
			}}
		>
			{children}
		</ErrorContext.Provider>
	);
};
